import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Container } from 'reactstrap';

import ContentPopups from '../../components/ad/ContentPopups';
import MainBanner from '../../components/ad/MainBanner';
import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import ScrollReset from '../../components/common/ScrollReset';
import AlternateLink from '../../components/head/AlternateLink';
import CanonicalLink from '../../components/head/CanonicalLink';
import SEOTitle from '../../components/head/SEOTitle';
import GenericLayout from '../../components/layout/GenericLayout';
import Page from '../../components/layout/Page';
import MainProductList from '../../components/product-list/MainProductList';
import useShouldShowBanner from '../../hooks/banner/useShouldShowBanner';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AdStore from '../../store/AdStore';
import ConfigStore from '../../store/ConfigStore';
import SectionStore from '../../store/SectionStore';
import AdPages from '../../types/AdPages';
import AdZones from '../../types/AdZones';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import ProductSortOrderBy from '../../types/ProductSortOrderBy';
import SortOrder from '../../types/SortOrder';

const PopularProductsPage = ({
  adStore,
  configStore,
  sectionStore,
  intl,
  routeService,
}) => {
  useEffect(() => {
    if (useShouldShowBanner({ adStore, page: AdPages.POPULAR_PRODUCTS })) {
      loadAds();
    }
  }, []);

  const loadAds = () => {
    adStore.loadAds(getSearchParams());
  };

  const getBreadcrumbsPath = () => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.mostPopularTitle),
        url: Paths.PopularProducts,
      },
    ]);
  };

  const getSearchParams = () => ({
    bannerPage: AdPages.POPULAR_PRODUCTS,
  });

  const listId = 'popular_products_list';
  const listName = 'Popular Products List';
  const fixedParams = {
    onlySoldProducts: 1,
    orderBy: ProductSortOrderBy.SALES,
    order: SortOrder.DESC,
  };
  if (sectionStore.activeSection) {
    fixedParams.sections = [sectionStore.activeSection.id];
  }

  const mainBanner = useShouldShowBanner({
    adStore,
    page: AdPages.POPULAR_PRODUCTS,
    zone: AdZones.MAIN_BANNER,
  }) ? (
    <MainBanner searchParams={getSearchParams()} />
  ) : null;
  const mainBannerWithinContent =
    configStore.banner.mainBanner.withinContent &&
    !configStore.banner.mainBanner.fullWidth;

  return (
    <Page name={CommonPage.POPULAR_PRODUCTS} className="PopularProductsPage">
      <ScrollReset />
      <SEOTitle
        title={{
          ...globalTranslations.mostPopularTitle,
        }}
      />
      <CanonicalLink
        path={routeService.getPath(
          Paths.PopularProducts,
          sectionStore.activeSection
        )}
      />
      <AlternateLink path={Paths.PopularProducts} />
      {!mainBannerWithinContent && mainBanner}
      <Container className="PopularProductsPage__container">
        <GenericLayout
          bannerSearchParams={
            useShouldShowBanner({
              adStore,
              page: AdPages.POPULAR_PRODUCTS,
              zone: AdZones.SIDEBAR_BANNER,
            })
              ? getSearchParams()
              : null
          }
          content={
            <>
              {mainBannerWithinContent && mainBanner}
              <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
              <section
                className="PopularProductsPage__main-content"
                aria-label={intl.formatMessage(
                  globalTranslations.pageContentRegionLabel
                )}
              >
                <PageTitle>
                  <FormattedMessage {...globalTranslations.mostPopularTitle} />
                </PageTitle>
              </section>
              <MainProductList
                fixedParams={fixedParams}
                listId={listId}
                name={listName}
                showSort={false}
              />
              {useShouldShowBanner({
                adStore,
                page: AdPages.POPULAR_PRODUCTS,
                zone: AdZones.POPUP_CONTENT,
              }) && <ContentPopups />}
            </>
          }
        />
      </Container>
    </Page>
  );
};

PopularProductsPage.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
};

export default injectIntl(
  inject(
    'adStore',
    'configStore',
    'sectionStore',
    'routeService'
  )(observer(PopularProductsPage))
);
